import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { specLabel, ALL_SPECIALISMS } from "@/lib/specialisms";
import { CreatorAgreementText, AGREEMENT_VERSION } from "@/components/yc/CreatorAgreement";
import { toast } from "sonner";
import { Lock, Film, Camera, X } from "lucide-react";

const EMPTY = {
    name: "", city: "", telephone: "", contact_email: "",
    job_title: "", job_function: "",
    specialisms: [],
    rate_day_min: "", rate_day_mid: "", rate_day_high: "", current_salary: "",
    profile_picture: "",
    media_links: ["", "", "", "", ""],
    showreel_link: "",
};

const CreatorOnboarding = () => {
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [alreadyAgreed, setAlreadyAgreed] = useState(false);

    useEffect(() => {
        api.get("/creator/profile").then((r) => {
            const d = r.data || {};
            const links = [...(d.media_links || [])];
            while (links.length < 5) links.push("");
            setForm({
                name: d.name || "",
                city: d.city || d.location || "",
                telephone: d.telephone || "",
                contact_email: d.contact_email || "",
                job_title: d.job_title || "",
                job_function: d.job_function || "",
                specialisms: d.specialisms || [],
                rate_day_min: d.rate_day_min || "",
                rate_day_mid: d.rate_day_mid || "",
                rate_day_high: d.rate_day_high || "",
                current_salary: d.current_salary || "",
                profile_picture: d.profile_picture || "",
                media_links: links.slice(0, 5),
                showreel_link: d.showreel_link || "",
            });
            if (d.agreement_accepted_at) setAlreadyAgreed(true);
        });
    }, []);

    const toggleSpec = (s) => {
        setForm((f) => {
            const has = f.specialisms.includes(s);
            if (has) return { ...f, specialisms: f.specialisms.filter((x) => x !== s) };
            if (f.specialisms.length >= 3) {
                toast.error("You can select up to 3 specialisms");
                return f;
            }
            return { ...f, specialisms: [...f.specialisms, s] };
        });
    };

    const setLink = (i, v) => {
        const next = [...form.media_links];
        next[i] = v;
        setForm({ ...form, media_links: next });
    };

    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
        if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
        const reader = new FileReader();
        reader.onload = () => setForm((f) => ({ ...f, profile_picture: reader.result }));
        reader.readAsDataURL(file);
    };

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Please add your name"); return; }
        if (!(agreed || alreadyAgreed)) {
            toast.error("Please read and accept the Creator Agreement to continue");
            return;
        }
        setSaving(true);
        try {
            const num = (v) => (v === "" || v === null ? 0 : parseInt(v, 10) || 0);
            await api.post("/creator/onboarding", {
                name: form.name,
                city: form.city,
                location: form.city,
                telephone: form.telephone,
                contact_email: form.contact_email,
                job_title: form.job_title,
                job_function: form.job_function,
                specialisms: form.specialisms,
                rate_day_min: num(form.rate_day_min),
                rate_day_mid: num(form.rate_day_mid),
                rate_day_high: num(form.rate_day_high),
                current_salary: num(form.current_salary),
                profile_picture: form.profile_picture,
                media_links: form.media_links.filter((x) => x.trim()),
                showreel_link: form.showreel_link,
                agreement_accepted: true,
            });
            toast.success("Profile saved");
            navigate("/creator/dashboard");
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : (detail || "Could not save — check your details"));
        } finally { setSaving(false); }
    };

    return (
        <DashboardShell role="creator" title="Your profile" subtitle="This is your profile. Only you can see and edit it.">
            <div className="max-w-3xl mx-auto w-full grid grid-cols-1 gap-6">

                {/* Profile picture + basics */}
                <div className="yc-card p-8 space-y-5" data-testid="profile-basics-card">
                    <h2 className="font-heading text-2xl font-bold">About you</h2>
                    <div className="flex items-center gap-5">
                        <div className="w-24 h-24 rounded-2xl border-2 border-yc-ink overflow-hidden bg-white flex items-center justify-center shrink-0" data-testid="profile-picture-preview">
                            {form.profile_picture
                                ? <img src={form.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                                : <Camera className="w-7 h-7 text-yc-ink/30" />}
                        </div>
                        <div>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="profile-picture-input" onChange={onPickImage} />
                            <button type="button" className="yc-btn-secondary text-sm py-2 px-4" data-testid="profile-picture-btn" onClick={() => fileRef.current?.click()}>
                                {form.profile_picture ? "Change photo" : "Upload photo"}
                            </button>
                            {form.profile_picture && (
                                <button type="button" className="ml-2 text-sm text-yc-ink/60 underline" data-testid="profile-picture-remove" onClick={() => setForm({ ...form, profile_picture: "" })}>Remove</button>
                            )}
                            <p className="text-xs text-yc-ink/55 mt-2">JPG or PNG, up to 3MB.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="yc-overline">Name *</label>
                            <input data-testid="cob-name" className="yc-input mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">City</label>
                            <input data-testid="cob-city" className="yc-input mt-2" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Current job title</label>
                            <input data-testid="cob-job-title" className="yc-input mt-2" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Job function</label>
                            <input data-testid="cob-job-function" className="yc-input mt-2" placeholder="e.g. Videographer, Designer" value={form.job_function} onChange={(e) => setForm({ ...form, job_function: e.target.value })} />
                        </div>
                    </div>
                </div>

                {/* Contact */}
                <div className="yc-card p-8 space-y-5" data-testid="contact-card">
                    <div>
                        <h2 className="font-heading text-2xl font-bold">Contact</h2>
                        <p className="text-sm text-yc-ink/70 mt-1 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Private. Revealed to a brand only when they unlock your profile.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="yc-overline">Telephone number</label>
                            <input data-testid="cob-telephone" type="tel" className="yc-input mt-2" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Email address</label>
                            <input data-testid="cob-contact-email" type="email" className="yc-input mt-2" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                        </div>
                    </div>
                </div>

                {/* Specialisms */}
                <div className="yc-card p-8" data-testid="specialisms-card">
                    <h2 className="font-heading text-2xl font-bold">Specialisms</h2>
                    <p className="text-sm text-yc-ink/70 mt-1">Select up to 3. <span className="font-semibold">{form.specialisms.length}/3 selected</span></p>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {ALL_SPECIALISMS.map((s) => {
                            const active = form.specialisms.includes(s);
                            return (
                                <button key={s} type="button" data-testid={`spec-${s}`}
                                    onClick={() => toggleSpec(s)}
                                    className={`yc-badge ${active ? "bg-yc-blue text-white" : "bg-white"}`}>{specLabel(s)}</button>
                            );
                        })}
                    </div>
                </div>

                {/* Rate card + salary */}
                <div className="yc-card p-8 space-y-5" data-testid="rate-card">
                    <div>
                        <h2 className="font-heading text-2xl font-bold">Rate card</h2>
                        <p className="text-sm text-yc-ink/70 mt-1 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Day rates in GBP. Private until a brand unlocks your profile.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="yc-overline">Minimum day rate (£)</label>
                            <input data-testid="cob-rate-min" type="number" min="0" className="yc-input mt-2" value={form.rate_day_min} onChange={(e) => setForm({ ...form, rate_day_min: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Medium day rate (£)</label>
                            <input data-testid="cob-rate-mid" type="number" min="0" className="yc-input mt-2" value={form.rate_day_mid} onChange={(e) => setForm({ ...form, rate_day_mid: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">High day rate (£)</label>
                            <input data-testid="cob-rate-high" type="number" min="0" className="yc-input mt-2" value={form.rate_day_high} onChange={(e) => setForm({ ...form, rate_day_high: e.target.value })} />
                        </div>
                    </div>
                    <div className="md:w-1/3">
                        <label className="yc-overline">Current salary (£ / year)</label>
                        <input data-testid="cob-salary" type="number" min="0" className="yc-input mt-2" value={form.current_salary} onChange={(e) => setForm({ ...form, current_salary: e.target.value })} />
                    </div>
                </div>

                {/* Work links */}
                <div className="yc-card p-8" data-testid="work-links-card">
                    <div className="flex items-center gap-2">
                        <Film className="w-5 h-5 text-yc-pink" />
                        <h2 className="font-heading text-2xl font-bold">Your work</h2>
                    </div>
                    <p className="text-sm text-yc-ink/70 mt-1">Add up to 5 links to your videos or images, plus one showreel or portfolio link.</p>
                    <div className="mt-4 space-y-3">
                        <label className="yc-overline">Links to videos / images (up to 5)</label>
                        {form.media_links.map((lnk, i) => (
                            <div key={`link-${i}`} className="flex items-center gap-2">
                                <input data-testid={`cob-media-link-${i}`} className="yc-input" placeholder={`https://link-${i + 1}`} value={lnk} onChange={(e) => setLink(i, e.target.value)} />
                                {lnk && <button type="button" className="text-yc-ink/50 hover:text-yc-ink" data-testid={`cob-media-clear-${i}`} onClick={() => setLink(i, "")}><X className="w-4 h-4" /></button>}
                            </div>
                        ))}
                        <div className="pt-2">
                            <label className="yc-overline">Showreel or portfolio link</label>
                            <input data-testid="cob-showreel" className="yc-input mt-1" placeholder="https://your-showreel-or-portfolio" value={form.showreel_link} onChange={(e) => setForm({ ...form, showreel_link: e.target.value })} />
                        </div>
                    </div>
                </div>

                {/* Creator Agreement */}
                {!alreadyAgreed && (
                    <div className="yc-card p-8" data-testid="agreement-card">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div>
                                <h2 className="font-heading text-2xl font-bold">Creator Agreement</h2>
                                <p className="text-sm text-yc-ink/70 mt-1">Please read carefully — you must accept to join the network. <a href="/creator-agreement" target="_blank" rel="noreferrer" className="text-yc-blue font-semibold underline">Open full page</a></p>
                            </div>
                            <span className="yc-badge bg-yc-ink text-white">Required</span>
                        </div>
                        <div className="border-2 border-yc-ink rounded-2xl bg-white mt-5 p-6 max-h-80 overflow-y-auto" data-testid="agreement-scroll">
                            <CreatorAgreementText />
                        </div>
                        <label className="flex items-start gap-3 mt-5 cursor-pointer">
                            <input type="checkbox" data-testid="agreement-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0" />
                            <span className="text-sm font-semibold">
                                I have read and agree to the Creator Agreement ({AGREEMENT_VERSION}) between me and Nova Media Group LTD (trading as Curated Chaos), including the IP, non-compete, conduct and confidentiality terms.
                            </span>
                        </label>
                    </div>
                )}

                <button data-testid="cob-submit" disabled={saving} className="yc-btn-primary self-start" onClick={submit}>{saving ? "Saving…" : "Save profile"}</button>
            </div>
        </DashboardShell>
    );
};

export default CreatorOnboarding;
